<template>
  <div class="bottom-bar" :class="{textareaFocus: isTextAreaFocus}">
    <QuickActionsDialog btn-class="conversation-btn"
                        :actions="quickActions"
                        :entity="entity"
                        :icon-color="quickActionsBtnColor"
                        :isMobile="isMobile"
                        @actionClicked="openQuickActionsDialog"/>

    <div class="bottom-bar__textarea">
      <div class="bottom-bar__textarea-counter" :class="{error: isSmallScreen && smsLimitExceeded}">
        <textarea-autosize class="action-item textarea"
                           color="#596671"
                           ref="myTextarea"
                           v-model="text"
                           :placeholder="$t('client.matters.conversation.type_a_message')"
                           :rows=1
                           :min-height="15"
                           :max-height="350"
                           @input="textAreaChange"
                           @focus.native="isTextAreaFocus = true"
                           @blur.native="isTextAreaFocus = false" />

        <TextCounterLimiter v-if="channel === 'sms'"
                            :hide="isSmallScreen"
                            :text="text"
                            :limit="smsTextLimit"
                            @textLimitExceeded="onLimitExceeded" />
        <div class="emoji_picker_container" v-if="hasEmojisFF && !isMobile">
          <VcitaEmojiPicker :isSmallScreen="isSmallScreen" @emoji-selected="addEmojiToText"/>
        </div>
      </div>
      <span v-if="isSmallScreen && smsLimitExceeded" class="bottom-bar__textarea--warning">{{$t('common.errors.too_long_sms_shorter')}}</span>
    </div>

    <div class="channels" v-if="readyForChannelSelector && channels.length > 1">
      <ChannelSelector :currentChannel="channel"
                       :channels="parsedChannels"
                       :isSmsAvailable="isSmsAvailable"
                       :isSmsEnabled="isSmsEnabled"
                       :isMobile="isMobile"
                       @click="userUpdateChannel"/>

    </div>
    <v-btn class="action-item btn-send"
           rounded fab depressed
           :class="{'disabled': isLoading}"
           :color="secondaryColor"
           :disabled="disabledSendBtn"
           @click="sendMessage">
      <v-icon :color="textColor">icon-Mail-Send</v-icon>
    </v-btn>

  </div>
</template>

<script>
import Vue from "vue";
import {mapGetters} from "vuex";
import QuickActionsDialog from '@/modules/matters/components/quickActions/QuickActionsDialog'
import TextareaAutosize from 'vue-textarea-autosize'
import {TextCounterLimiter} from "@/modules/common/components"
import ChannelSelector from "@/modules/matters/components/conversation/ChannelSelector";
import VcitaEmojiPicker from "@/modules/common/components/VcitaEmojiPicker/VcitaEmojiPicker";
import debounce from 'just-debounce';
import {isTypingService} from "@/modules/common/services";

Vue.use(TextareaAutosize)

export default {
  name: 'ConversationFooter',
  components: {
    VcitaEmojiPicker,
    QuickActionsDialog,
    TextCounterLimiter,
    ChannelSelector,
  },
  props: {
    readyForChannelSelector: {
      type: Boolean,
      required: true
    },
    isLoading: {
      type: Boolean,
      required: true
    },
    isSmallScreen: {
      type: Boolean,
      required: false
    },
    isPane: {
      type: Boolean,
      default: false
    }
  },
  async created() {
    this.$frontageApi.listen('get_dedicated_number', this.fetchActiveDedicatedNumber);
    this.currContact && await this.$store.dispatch({type: 'accountModule/loadChannels', payload: {clientUid: this.currContact.uid}})

    await this.fetchActiveDedicatedNumber()
    if(this.inboxReadingPaneFF) {
      this.$refs?.myTextarea?.$el.focus()
    }
  },
  data() {
    return {
      isTextAreaFocus: false,
      text: '',
      channel: 'email',
      smsTextLimit: 550,
      smsLimitExceeded: false,
    }
  },
  watch: {
    messages() {this.systemUpdateChannel()},
    async currContact() {
      await this.$store.dispatch({type: 'accountModule/loadChannels', payload: {clientUid: this.currContact.uid}})
      this.systemUpdateChannel()
    },
    activeDedicatedNumber() {this.systemUpdateChannel()},
    channels() {this.systemUpdateChannel()}
  },
  computed: {
    ...mapGetters(['isMobile', 'direction', 'angularOrigin']),
    ...mapGetters('accountModule', ['channels', 'settings', 'activeDedicatedNumber', 'integrationsSettings', 'isStar']),
    ...mapGetters('brandModule', ['textColor', 'secondaryColor']),
    ...mapGetters('mattersModule', ['messages', 'matter', 'currContact']),
    ...mapGetters('quickActionsModule', ['pick']),
    inboxReadingPaneFF() {
      return this.$hasFeature('inbox_reading_pane')
    },
    communicationChannelUiFF() {
      return this.$hasFeature('communication_channel_ui')
    },
    hasEmojisFF() {
      return this.$hasFeature('emojis')
    },
    hasClaimDedicatedNumberFF() {
      return !!this.$hasFeature('claim_dedicated_number')
    },
    hasZipwhipEnabled() {
      return this.hasZipwhipFF && this.twoWayTextingActive
    },
    hasZipwhipFF() {
      return !!this.$hasFeature('zipwhip')
    },
    twoWayTextingActive() {
      return !!this.settings.two_way_texting_active
    },
    isSmsAvailable() {
      return !!this.currContact &&
        !!this.currContact.notifications_phone &&
        !!this.currContact.notifications_phone.value &&
        this.currContact.is_notifications_phone_mobile;
    },
    isSmsAllowed() {
      return !this.activeDedicatedNumber && !this.twoWayTextingActive;
    },
    hasTwoWayTextingEnabled() {
      return this.hasClaimDedicatedNumberFF && !!this.activeDedicatedNumber
    },
    isSmsEnabled() {
      return this.hasZipwhipEnabled || this.hasTwoWayTextingEnabled
    },
    disabledSendBtn() {
      return this.channel === 'sms' && this.isSmsAllowed;
    },
    quickActionsBtnColor() {
      const isSecondaryColorSet = this.isSecondaryColorSet
      return isSecondaryColorSet ? this.secondaryColor : undefined
    },
    hasScheduleFF() {
      return this.$hasFeature('online_scheduling')
    },
    hasPaymentFF() {
      return this.$hasFeature('payments_module')
    },
    hasClientTypingIndicatorFF() {
      return this.$hasFeature('client_typing_indicator')
    },
    hasNewShareDialog() {
      return this.$hasFeature('new_share_dialog');
    },
    hasEsign() {
      return this.$hasFeature('e_sign');
    },
    quickActions() {
      const actions = ['appointment', 'register_to_event', 'accept_payment', 'record_payment', 'invoice', 'portal', 'estimate']

      if (this.hasNewShareDialog) {
        actions.push('share_document', 'internal_document');
      } else {
        actions.push('document');
      }

      if (this.hasEsign) {
        actions.push('request_signature');
      }

      if(this.hasScheduleFF) {
        actions.push('schedule')
      }
      if(this.hasPaymentFF) {
        actions.push('pay')
      }
      return this.pick(actions)
    },
    entity() {
      return {
        fullName: this.matter.display_name,
        uid: this.matter.uid,
        colorId: 9,
        text: this.text
      }
    },
    parsedChannels() {
      let parsingChannels = this.channels.map(channel => {
        let active;
        if(channel.type === 'temporary') {
          if(!this.communicationChannelUiFF) return

          channel.disabled = !channel.installed
          active = channel.status
        }

        if(channel.disabled) {
          channel.tooltip = this.$t('client.matters.conversation.channels.uninstall')
        }

        if(channel.value === 'sms') {
          if (!this.isSmsAvailable && this.isSmsEnabled) {
            channel.tooltip = this.$t('common.errors.numeric')
            channel.disabled = true
          } else {
            channel.tooltip = false
            channel.disabled = false
          }
        }

        return {
          ...channel,
          text: channel.app_name,
          level: channel.type === 'core' ? 1 : 2,
          active
        }
      }).filter(onlyAvailableChannels => onlyAvailableChannels);

      if(this.communicationChannelUiFF && this.isStar && this.integrationsSettings.app_market_url) {
        // this need better handling on the app market side so when i trigger action for app market,
        // the app market component will decide if redirect, open dialog or do what ever its need to be done
        parsingChannels.push({
          text: this.$t('client.matters.conversation.channels.app_store'),
          level: 3,
          icon: 'icon-Channel',
          link: '/app/app-market'
        })
      }

      // ordering uninstall and install app a-z to each group inside level 2
      const sortByText = (a, b) => {
        if(a.text.toLowerCase() > b.text.toLowerCase()) {
          return 1
        } else if (a.text.toLowerCase() === b.text.toLowerCase()) {
          return 0
        }
        return -1
      }
      const disabledChannels = parsingChannels.filter(channel => channel.level === 2 && channel.disabled)
      const enabledChannels = parsingChannels.filter(channel => channel.level === 2 && !channel.disabled)
      const otherChannels = parsingChannels.filter(channel => channel.level !== 2)

      const disabledChannelsSorted = disabledChannels.sort(sortByText)
      const enabledChannelsSorted = enabledChannels.sort(sortByText)

      let lastKnownPosition = 0;
      enabledChannelsSorted.forEach((channel, i) => channel.position = lastKnownPosition = i)
      disabledChannelsSorted.forEach(channel => channel.position = ++lastKnownPosition)

      parsingChannels = [...otherChannels, ...enabledChannels, ...disabledChannels]

      return parsingChannels;
    },
    currentChannel() {
      return this.channels.find(channel => channel.value === this.channel)
    },
    businessStartTyping() {
      const sendImmediately = true;
      const sendWhenTimesUpAgain = true; // keep is typing alive in case of long text
      return debounce(() => isTypingService.businessIsTyping({
        contact: {
          source: 'business',
          uid: this.currContact.uid,
          external_uid: this.currentChannel.external_uid,
          channel_uid: this.currentChannel.channel_uid,
          typing: true,
        }
      }), 30000, sendImmediately, sendWhenTimesUpAgain)
    },
    businessStopTyping() {
      return debounce(() => isTypingService.businessIsTyping({
        contact: {
          source: 'business',
          uid: this.currContact.uid,
          external_uid: this.currentChannel.external_uid,
          channel_uid: this.currentChannel.channel_uid,
          typing: false,
        }
      }), 31000)
    },
    inboxReadingPaneMixpanelFF() {
      return this.$hasFeature('inbox_reading_pane_mixpanel')
    }
  },
  methods: {
    async fetchActiveDedicatedNumber() {
      this.$store.dispatch({type:'accountModule/fetchActiveDedicatedNumber'})
    },
    openQuickActionsDialog(action) {
      this.$frontageApi.send('quick-actions-event', {action: action.name, data: {entity: this.entity}})
    },
    async sendMessage() {
      if (!this.text) return
      if (this.channel === 'sms' &&  this.smsLimitExceeded) {
        this.$frontageApi.send('sms-too-long')
        return
      }

      this.$emit('sending-message', true)
      const message = {text: this.text, channels: [this.channel]}
      await this.$store.dispatch({type: 'mattersModule/sendMessage', payload: {message}})

      if(this.inboxReadingPaneMixpanelFF) {
        const trackOrigin = this.isPane ? `Right pane matter card - ${this.angularOrigin}` : 'Full page matter card';
        this.$track('Conversation page message - success', {selected_channel: this.channel, Origin: trackOrigin})
      } else {
        this.$track('Conversation page message - success', {selected_channel: this.channel})
      }

      this.text = ''
      this.$emit('sending-message', false)
    },
    onLimitExceeded(exceeded) {
      this.smsLimitExceeded = exceeded
    },
    addEmojiToText(emoji) {
      this.text = `${this.text} ${emoji} `
    },
    userUpdateChannel(channel) {
      if(channel.level === 3) return;
      if(channel.value === 'sms' && !this.isSmsAvailable) return;
      this.channel = channel.value
    },
    systemUpdateChannel() {
      if(this.messages) {
        let lastMassageOrigin = this.messages[0] && this.messages[0].channels && this.messages[0].channels[0] || 'email';
        if (lastMassageOrigin === 'sms') {
          if (!this.isSmsAvailable || !this.isSmsEnabled) {
            lastMassageOrigin = 'email';
          }
        } else if (lastMassageOrigin !== 'email') {
          const activeChannel = this.channels.find(channel => channel.value === lastMassageOrigin)
          if(!activeChannel || activeChannel.disabled) {
            lastMassageOrigin = 'email';
          } else if (activeChannel.status === 'inactive') {
            const nextActiveChannel = this.channels.find(channel => channel.status === 'active')
            if(nextActiveChannel) {
              lastMassageOrigin = nextActiveChannel.value
            } else {
              lastMassageOrigin = 'email';
            }
          }
        }
        this.channel = lastMassageOrigin;
      }
    },
    textAreaChange() {
      if(this.hasClientTypingIndicatorFF &&
        !['email','sms'].includes(this.channel) &&
        this.currentChannel?.external_uid && this.currentChannel?.channel_uid) {
        this.businessStartTyping();
        this.businessStopTyping();
      }
    },
  },
  destroyed() {
    this.$frontageApi.remove('get_dedicated_number', this.fetchActiveDedicatedNumber);
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/abstract/_mixins.scss";
@import "@/assets/scss/abstract/_variables.scss";

.bottom-bar {
  display: flex;
  align-items: flex-start;
  border-top: 1px solid $grey-color-2;
  width: 100%;
  padding: 12px;
  background-color: $white-color-1;

  .emoji_picker_container {
    margin-inline-end: 10px;
  }
  .chromeIos.iPad &.textareaFocus {
    margin-bottom: 30px;
  }

  &__textarea{
    flex-grow: 1;

    &-counter {
      position: relative;
      display: flex;
      align-items: flex-end;
      margin-inline-start: 12px;
      background-color: $grey-color-18;
      border-radius: 30px;
      border: 1px solid transparent;

      ::-webkit-scrollbar {
        width: 0;
      }

      &.error {
        border: 1px solid $status_error;
        background-color: $grey-color-18 !important;
      }
    }

    &--warning {
      color: $status_error;
      font-size: 10px;
      padding-inline-start: 26px;
    }
  }

  .v-tooltip {
    z-index: 100;
  }

  .conversation-btn {
    width: 36px;
    height: 36px;

    .v-icon {
      font-size: 20px;
    }
  }

  .action-item {
    margin-inline-start: 12px;

    &.textarea {
      min-height: 36px;
      padding: 5px 10px;
      font-family: var(--primary-font-family);
      font-size: 12px;
      font-weight: 500;
      min-width: 150px;
      max-height: 300px;
      width: 100%;
      line-height: 1.55rem;
      outline: none;
      overflow: auto !important;

      ::-webkit-scrollbar-thumb {
        display: none;
      }

      @include sm-and-down {
        max-height: 112px;
      }

    }

    &.btn-send {
      transform: scaleX(-1);
      width: 40px;
      height: 40px;

      .v-icon {
        height: 16px;
        font-size: 16px;
        width: 16px;
      }
      &.disabled {
        pointer-events: none;
      }
    }
  }
}

.channels {
  margin-inline-start: 12px;
}
</style>
